import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Field, FormProgress, FormSection, Tile, areaCls, inputCls } from "@/components/forms/FormKit";
import { ArrowUpRight, BadgeCheck, Building2, CheckCircle2, GraduationCap, Handshake, Languages, Mail, MessageSquare, Phone, UserRound, UserRoundCheck, Users } from "lucide-react";

const EMPTY = { company_name: "", contact_name: "", designation: "", email: "", phone: "", interest: "training", team_size: "", message: "" };
const INTERESTS = [
  { v: "training", l: "Training my team", Icon: GraduationCap },
  { v: "hiring", l: "Hiring your talent", Icon: UserRoundCheck },
  { v: "swiss-german", l: "Swiss German course", Icon: Languages },
  { v: "both", l: "Both", Icon: Handshake },
];
const filled = (v) => String(v || "").trim().length > 0;
const emailOk = (v) => /\S+@\S+\.\S+/.test(v);

export function CompanyInquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const done = [filled(form.company_name), filled(form.contact_name) && emailOk(form.email), filled(form.message)];

  const submit = async (e) => {
    e.preventDefault();
    if (!done[0] || !done[1]) { toast.error("Please fill company name, your name and a valid email"); return; }
    setSending(true);
    try {
      await api.post("/companies/inquiry", form);
      setSent(true);
      toast.success("Thanks! We'll reach out within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit. Please try again.");
    } finally { setSending(false); }
  };

  if (sent) return <InquirySent email={form.email} onAgain={() => { setForm(EMPTY); setSent(false); }} />;

  return (
    <form onSubmit={submit} data-testid="companies-inquiry-form" className="md:col-span-3 relative rounded-3xl border border-amber-500/30 bg-gradient-to-br from-[#1c1608] via-[#111009] to-[#0b0a08] p-5 sm:p-8 shadow-[0_0_60px_-15px_rgba(245,158,11,0.35)]">
      <div aria-hidden className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-amber-400/80 to-transparent" />
      <FormProgress done={done.filter(Boolean).length} total={3} />
      <FormSection n="01" title="About your company" done={done[0]} testid="companies-section-company">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Company name" htmlFor="ci-company" required icon={Building2} filled={done[0]}>
            <input id="ci-company" data-testid="company-name-input" value={form.company_name} onChange={set("company_name")} placeholder="Acme Inc." className={inputCls} />
          </Field>
          <Field label="Team size" htmlFor="ci-team" optional icon={Users} filled={filled(form.team_size)}>
            <input id="ci-team" data-testid="contact-team-size-input" value={form.team_size} onChange={set("team_size")} placeholder="e.g. 25" className={inputCls} />
          </Field>
        </div>
        <div className="mt-5 text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-400">I&apos;m interested in</div>
        <div className="mt-1.5 grid grid-cols-2 gap-2.5">
          {INTERESTS.map(({ v, l, Icon }) => (
            <Tile key={v} testid={`contact-interest-${v}`} on={form.interest === v} onClick={() => setForm({ ...form, interest: v })} icon={Icon} title={l} />
          ))}
        </div>
      </FormSection>
      <FormSection n="02" title="Your details" done={done[1]} testid="companies-section-you">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Your name" htmlFor="ci-name" required icon={UserRound} filled={filled(form.contact_name)}>
            <input id="ci-name" data-testid="contact-name-input" value={form.contact_name} onChange={set("contact_name")} placeholder="Priya Sharma" className={inputCls} />
          </Field>
          <Field label="Your designation" htmlFor="ci-role" optional icon={BadgeCheck} filled={filled(form.designation)}>
            <input id="ci-role" data-testid="contact-designation-input" value={form.designation} onChange={set("designation")} placeholder="Head of L&D / HR Manager" className={inputCls} />
          </Field>
          <Field label="Work email" htmlFor="ci-email" required icon={Mail} filled={emailOk(form.email)}>
            <input id="ci-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder="priya@acme.com" className={inputCls} />
          </Field>
          <Field label="Phone" htmlFor="ci-phone" optional icon={Phone} filled={filled(form.phone)}>
            <input id="ci-phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 98xxxxxxx" className={inputCls} />
          </Field>
        </div>
      </FormSection>
      <FormSection n="03" title="What are you solving for?" hint="Skills, roles or timelines. Optional, but it helps." done={done[2]} last testid="companies-section-message">
        <Field label="Tell us more" htmlFor="ci-msg" icon={MessageSquare} top filled={done[2]}>
          <textarea id="ci-msg" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Which skills, roles, or timelines?" className={areaCls} />
        </Field>
      </FormSection>
      <button data-testid="companies-inquiry-submit" type="submit" disabled={sending}
        className="group w-full mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black font-semibold px-6 py-3.5 text-sm glow-amber transition-smooth">
        {sending ? "Sending…" : <>Send enquiry <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
      </button>
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-adult-mono text-center pt-3">We reply within 24 hours</div>
    </form>
  );
}

function InquirySent({ email, onAgain }) {
  return (
    <div data-testid="companies-inquiry-success" className="md:col-span-3 rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-8 md:p-10 animate-in fade-in zoom-in-95 duration-300">
      <CheckCircle2 className="w-10 h-10 text-emerald-400" />
      <h3 className="mt-5 font-adult-heading text-3xl font-semibold tracking-tight">Thanks, your enquiry is in.</h3>
      <p className="mt-3 text-zinc-300 leading-relaxed">We&apos;ll reach out to <span className="text-white">{email}</span> within 24 hours.</p>
      <button type="button" data-testid="companies-inquiry-again" onClick={onAgain} className="mt-6 inline-flex rounded-full border border-zinc-700 hover:border-zinc-500 px-5 py-2.5 text-sm">Send another enquiry</button>
    </div>
  );
}
